<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
        Micro-Credentials Management
        <BaseInfoIcon :text="ERKLAERUNG.microCredentials" label="Micro-Credentials" />
      </h2>
      <BaseButton @click="openCreateDialog">
        + Create Micro-Credential
      </BaseButton>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      {{ errorMessage }}
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
      {{ successMessage }}
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <p class="text-gray-600">Loading micro-credentials...</p>
    </div>

    <!-- Credentials Table. overflow-x-auto statt overflow-hidden (01.10.):
         mit der Discord-Spalte war die Tabelle breiter als der Container,
         und overflow-hidden schnitt Users/Created/Actions STILL ab — auch
         bei 1920 px, ohne Scrollbalken. Jetzt passt sie, und wo nicht
         (schmaler Bildschirm), lässt sie sich scrollen. Die runden Ecken
         bleiben: jedes overflow ≠ visible schneidet am Radius. -->
    <div v-else class="bg-white rounded-lg shadow overflow-x-auto">
      <table class="min-w-full">
        <thead class="bg-gray-100">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              ID
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Name
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Description
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Discord-Server
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Users
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Created
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="credential in credentials" :key="credential.id" class="hover:bg-gray-50">
            <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ credential.id }}
            </td>
            <td class="px-4 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">{{ credential.name }}</div>
            </td>
            <td class="px-4 py-4">
              <!-- Zwei Zeilen statt einer abgeschnittenen (01.10.), der volle
                   Text im title. max-w hält die Spalte schmal genug, dass die
                   Tabelle ab 1440 px ohne Scrollen passt. -->
              <div class="text-sm text-gray-600 max-w-[14rem] line-clamp-2" :title="credential.description || ''">
                {{ credential.description || '-' }}
              </div>
            </td>
            <td class="px-4 py-4 text-sm text-gray-600">
              <!-- Name aus dem Cache, die ID darunter: die ID entscheidet,
                   der Name ist Anzeige. Ohne Server ein Strich. Gestapelt
                   statt nebeneinander (01.10.) — in einer Zeile mit nowrap
                   machte die 19-stellige ID die Tabelle zu breit. -->
              <div v-if="credential.discord_guild_id">
                <div class="whitespace-nowrap">{{ credential.discord_guild_name || '?' }}</div>
                <div class="text-xs text-gray-400 font-mono">{{ credential.discord_guild_id }}</div>
              </div>
              <span v-else>-</span>
            </td>
            <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              <button
                @click="viewStats(credential.id)"
                class="text-indigo-600 hover:text-indigo-900 underline"
              >
                View Stats
              </button>
            </td>
            <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ formatDate(credential.created_at) }}
            </td>
            <td class="px-4 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
              <BaseButton
                variant="edit"
                class="px-3 py-1"
                @click="openEditDialog(credential)"
              >
                Edit
              </BaseButton>
              <BaseButton
                variant="delete"
                class="px-3 py-1"
                @click="confirmDelete(credential)"
              >
                Delete
              </BaseButton>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="credentials.length === 0" class="text-center py-8 text-gray-500">
        No micro-credentials found. Create one to get started.
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <BaseDialog v-if="showDialog" @close="closeDialog">
      <template #title>
        {{ isEditMode ? 'Edit Micro-Credential' : 'Create Micro-Credential' }}
      </template>
      <template #content>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <BaseInput
              v-model="formData.name"
              placeholder="e.g., Data Science 2026"
              type="text"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="formData.description"
              class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows="3"
              placeholder="Optional description"
            ></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Discord-Server</label>
            <!-- Dieselbe Komponente wie im Tab „Discord-Server" — eine Stelle
                 für Leerstring→null, Zeichenketten-ID und Handeingabe. Beide
                 Listen werden beim Öffnen des Dialogs geholt (ladeGuilds),
                 nicht hier drin. -->
            <GuildWahl
              :key="`guildwahl-${guildsGeladen}`"
              :modelValue="formData.discord_guild_id"
              :guildName="formData.discord_guild_name"
              :guilds="guilds"
              :bekannte="bekannte"
              :botFehler="botFehler"
              @gewaehlt="setzeGuild"
            />
          </div>
        </div>
      </template>
      <template #actions>
        <BaseButton variant="cancel" @click="closeDialog">
          Cancel
        </BaseButton>
        <BaseButton @click="saveCredential" :disabled="saving || !formData.name">
          {{ saving ? 'Saving...' : 'Save' }}
        </BaseButton>
      </template>
    </BaseDialog>

    <!-- Stats Dialog -->
    <BaseDialog v-if="showStatsDialog" @close="closeStatsDialog">
      <template #title>
        Micro-Credential Statistics
      </template>
      <template #content>
        <div v-if="loadingStats" class="text-center py-4">
          <p class="text-gray-600">Loading statistics...</p>
        </div>
        <div v-else-if="selectedStats" class="space-y-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">{{ selectedStats.name }}</h3>
            <p class="text-sm text-gray-600">{{ selectedStats.description }}</p>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-blue-50 rounded-lg p-4">
              <p class="text-sm text-gray-600">Total Users</p>
              <p class="text-2xl font-bold text-blue-700">{{ selectedStats.stats.user_count }}</p>
            </div>
            <div class="bg-green-50 rounded-lg p-4">
              <p class="text-sm text-gray-600">Professors</p>
              <p class="text-2xl font-bold text-green-700">{{ selectedStats.stats.professor_count }}</p>
            </div>
            <div class="bg-purple-50 rounded-lg p-4">
              <p class="text-sm text-gray-600">Students</p>
              <p class="text-2xl font-bold text-purple-700">{{ selectedStats.stats.student_count }}</p>
            </div>
            <div class="bg-yellow-50 rounded-lg p-4">
              <p class="text-sm text-gray-600">Sessions</p>
              <p class="text-2xl font-bold text-yellow-700">{{ selectedStats.stats.session_count }}</p>
            </div>
            <div class="bg-pink-50 rounded-lg p-4">
              <p class="text-sm text-gray-600">Teams</p>
              <p class="text-2xl font-bold text-pink-700">{{ selectedStats.stats.team_count }}</p>
            </div>
            <div class="bg-indigo-50 rounded-lg p-4">
              <p class="text-sm text-gray-600">Criteria</p>
              <p class="text-2xl font-bold text-indigo-700">{{ selectedStats.stats.criteria_count }}</p>
            </div>
          </div>
        </div>
      </template>
      <template #actions>
        <BaseButton @click="closeStatsDialog">
          Close
        </BaseButton>
      </template>
    </BaseDialog>

    <!-- Bestätigung vor dem Ersetzen oder Lösen eines Servers
         (serverwechselBrauchtBestaetigung). ⚠️ Im DOM NACH dem BaseDialog:
         beide liegen auf z-50, bei gleichem z-index gewinnt das spätere
         Element -- sonst läge die Frage UNTER dem Bearbeiten-Dialog. -->
    <ConfirmModal
      :isOpen="!!serverFrage"
      :title="SERVERWAHL.titel"
      :confirmLabel="serverFrage && serverFrage.neu ? SERVERWAHL.knopfErsetzen : SERVERWAHL.knopfLoesen"
      confirmVariant="primary"
      cancelLabel="Abbrechen"
      @confirm="serverBestaetigt"
      @cancel="serverFrage = null"
    >
      <span v-if="serverFrage" class="inline-flex items-start gap-1.5 bestaetigung-satz">
        <span>{{ serverFrageSatz }}</span>
        <BaseInfoIcon :text="ERKLAERUNG.serverErsetzen" label="Was mit den Nachrichten geschieht" fixiert />
      </span>
    </ConfirmModal>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :isOpen="showDeleteConfirm"
      :title="'Delete Micro-Credential'"
      :message="`Are you sure you want to delete '${credentialToDelete?.name}'? This action cannot be undone.`"
      :confirmLabel="'Delete'"
      @confirm="deleteCredential"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import {
  getAllMicroCredentials,
  getMicroCredentialWithStats,
  createMicroCredential,
  updateMicroCredential,
  deleteMicroCredential,
} from '@/api/admin';
import BaseButton from '@/BaseComponents/BaseButton.vue';
import BaseInput from '@/BaseComponents/BaseInput.vue';
import BaseInfoIcon from '@/BaseComponents/BaseInfoIcon.vue';
import BaseDialog from '@/BaseComponents/BaseDialog.vue';
import ConfirmModal from '@/BaseComponents/ConfirmModal.vue';
import GuildWahl from './GuildWahl.vue';
import {
  SERVERWAHL,
  ladeBekannteServer,
  ladeDiscordServer,
  serverwechselBrauchtBestaetigung,
} from '@/async-sessions/discordServer';
import { ERKLAERUNG } from '@/async-sessions/erklaerungen';

export default {
  name: 'MicroCredentialList',
  components: {
    BaseButton,
    BaseInput,
    BaseInfoIcon,
    BaseDialog,
    ConfirmModal,
    GuildWahl,
  },
  data() {
    return {
      SERVERWAHL,
      ERKLAERUNG,
      credentials: [],
      // Die offene Bestätigung vor dem Ersetzen/Lösen: { alt, neu } als
      // Anzeigenamen, oder null.
      serverFrage: null,
      loading: false,
      saving: false,
      loadingStats: false,
      errorMessage: '',
      successMessage: '',
      showDialog: false,
      showStatsDialog: false,
      showDeleteConfirm: false,
      isEditMode: false,
      formData: {
        name: '',
        description: '',
        // Beide IMMER im Payload, auch als null: so wird ein Server gelöst.
        discord_guild_id: null,
        discord_guild_name: null,
      },
      // Die Server des Bots und die bekannten Server, beim Öffnen des
      // Dialogs geholt.
      guilds: [],
      bekannte: [],
      botFehler: '',
      guildsGeladen: 0,
      editingCredentialId: null,
      credentialToDelete: null,
      selectedStats: null,
    };
  },
  computed: {
    // Der eine Satz im Bestätigungsdialog.
    serverFrageSatz() {
      const f = this.serverFrage;
      if (!f) return '';
      return f.neu ? SERVERWAHL.ersetzt(f.alt, f.neu) : SERVERWAHL.geloest(f.alt);
    },
  },
  async mounted() {
    await this.loadCredentials();
  },
  methods: {
    async loadCredentials() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await getAllMicroCredentials();
        this.credentials = response.data;
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to load micro-credentials';
      } finally {
        this.loading = false;
      }
    },
    async viewStats(credentialId) {
      this.loadingStats = true;
      this.showStatsDialog = true;
      this.selectedStats = null;
      try {
        const response = await getMicroCredentialWithStats(credentialId);
        this.selectedStats = response.data;
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to load statistics';
        this.showStatsDialog = false;
      } finally {
        this.loadingStats = false;
      }
    },
    closeStatsDialog() {
      this.showStatsDialog = false;
      this.selectedStats = null;
    },
    leeresFormular() {
      return { name: '', description: '', discord_guild_id: null, discord_guild_name: null };
    },
    async ladeGuilds() {
      // Getrennt vom Dialog-Öffnen und ohne await dort: der Bot-Aufruf
      // läuft gegen MESSAGE_BOT_TIMEOUT und darf den Dialog nicht aufhalten.
      // Zuerst die bekannten Server (eigene Datenbank, schnell) -- nach
      // JEDER Liste zählt der key hoch (s. u.).
      const { bekannte } = await ladeBekannteServer();
      this.bekannte = bekannte;
      this.guildsGeladen += 1;
      const { guilds, fehler } = await ladeDiscordServer();
      this.guilds = guilds;
      this.botFehler = fehler;
      // GuildWahl entscheidet in data() einmalig zwischen Liste und
      // Handeingabe; der key zwingt sie, das mit der geladenen Liste neu zu tun.
      this.guildsGeladen += 1;
    },
    setzeGuild({ id, name }) {
      this.formData.discord_guild_id = id;
      this.formData.discord_guild_name = name;
    },
    openCreateDialog() {
      this.isEditMode = false;
      this.formData = this.leeresFormular();
      this.editingCredentialId = null;
      this.showDialog = true;
      this.ladeGuilds();
    },
    openEditDialog(credential) {
      this.isEditMode = true;
      this.formData = {
        name: credential.name,
        description: credential.description || '',
        discord_guild_id: credential.discord_guild_id || null,
        discord_guild_name: credential.discord_guild_name || null,
      };
      this.editingCredentialId = credential.id;
      this.showDialog = true;
      this.ladeGuilds();
    },
    closeDialog() {
      this.showDialog = false;
      this.formData = this.leeresFormular();
      this.editingCredentialId = null;
    },
    async saveCredential() {
      this.errorMessage = '';
      this.successMessage = '';
      if (this.isEditMode) {
        // Ersetzt oder löst das Speichern einen bestehenden Server, fragt
        // erst der Dialog; sonst direkt. EINE Entscheidungsfunktion für
        // beide Admin-Komponenten.
        const vorher = this.credentials.find((c) => c.id === this.editingCredentialId) || {};
        const alt = vorher.discord_guild_id || null;
        const neu = this.formData.discord_guild_id || null;
        if (serverwechselBrauchtBestaetigung(alt, neu)) {
          this.serverFrage = {
            alt: vorher.discord_guild_name || alt,
            neu: neu ? (this.formData.discord_guild_name || neu) : null,
          };
          return;
        }
      }
      await this.schreibeCredential();
    },
    async serverBestaetigt() {
      this.serverFrage = null;
      await this.schreibeCredential();
    },
    // Ein PATCH mit dem VOLLSTÄNDIGEN Formular inklusive Server -- seit
    // 01.10. der einzige Wechselweg. null in discord_guild_id löst.
    async schreibeCredential() {
      this.saving = true;
      try {
        if (this.isEditMode) {
          await updateMicroCredential(this.editingCredentialId, this.formData);
          this.successMessage = 'Micro-credential updated successfully';
        } else {
          await createMicroCredential(this.formData);
          this.successMessage = 'Micro-credential created successfully';
        }
        this.closeDialog();
        await this.loadCredentials();
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to save micro-credential';
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(credential) {
      this.credentialToDelete = credential;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.credentialToDelete = null;
      this.showDeleteConfirm = false;
    },
    async deleteCredential() {
      this.errorMessage = '';
      this.successMessage = '';
      try {
        await deleteMicroCredential(this.credentialToDelete.id);
        this.successMessage = `Micro-credential '${this.credentialToDelete.name}' deleted successfully`;
        this.showDeleteConfirm = false;
        this.credentialToDelete = null;
        await this.loadCredentials();
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      } catch (error) {
        this.errorMessage = error.response?.data?.detail || 'Failed to delete micro-credential';
        this.showDeleteConfirm = false;
        this.credentialToDelete = null;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>
